import { Link } from 'react-router-dom';
import { services } from '../../../mocks/services';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const serviceImages = [
  'https://public.readdy.ai/ai/img_res/b34b521904e6ec2322b9119bfc772d3c.jpg',
  'https://readdy.ai/api/search-image?query=HVAC%20professional%20technician%20cleaning%20air%20duct%20vent%20system%20inside%20modern%20Jacksonville%20home%2C%20powerful%20vacuum%20equipment%20attached%20to%20ceiling%20vent%2C%20clean%20air%20quality%20improvement%2C%20specialized%20tools%2C%20professional%20uniform%2C%20residential%20interior%20setting&width=800&height=600&seq=svc_duct_v3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=dryer%20vent%20cleaning%20technician%20using%20high-powered%20vacuum%20machine%20attached%20to%20white%20dryer%20vent%20exhaust%20pipe%2C%20lint%20removal%20fire%20prevention%2C%20laundry%20room%2C%20professional%20equipment%2C%20clean%20home%20safety%20service%2C%20clear%20bright%20lighting&width=800&height=600&seq=svc_dryer_v3&orientation=landscape',
];

export default function ServicesSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-28 bg-white" id="services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-4">
              <div className="h-px w-8 bg-brand-amber" />
              Core Services
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark leading-tight">
              Three Services That Keep<br />
              <span className="text-brand-amber">Your Home Safe & Healthy</span>
            </h2>
          </div>
          <p className="text-brand-warm-gray text-lg leading-relaxed max-w-md lg:text-right">
            Certified technicians. Professional equipment. Real results. Every service designed to protect your family's safety and air quality.
          </p>
        </div>

        {/* Service Cards — asymmetric staggered layout */}
        <div className="space-y-10">
          {services.map((service, index) => (
            <article
              key={service.id}
              className={`group relative flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} rounded-3xl overflow-hidden border border-stone-100 hover:border-brand-amber/30 transition-all duration-300`}
            >
              {/* Image — 45% width */}
              <div className="lg:w-[45%] h-72 lg:h-auto flex-shrink-0 overflow-hidden relative">
                <img
                  src={serviceImages[index]}
                  alt={`${service.title} service in Jacksonville FL`}
                  className="w-full h-full object-cover object-center group-hover:scale-103 transition-transform duration-700"
                />
                {/* Price badge */}
                <div className="absolute top-5 left-5">
                  <div className="bg-brand-dark/80 backdrop-blur-sm text-brand-amber text-xs font-bold px-3 py-1.5 rounded-full border border-brand-amber/30">
                    {service.price} · {service.duration}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`flex-1 p-8 lg:p-12 xl:p-14 flex flex-col justify-center bg-stone-50 group-hover:bg-amber-50/40 transition-colors duration-300`}>
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl mb-6 border border-stone-200 group-hover:border-brand-amber/30 group-hover:bg-amber-50 transition-colors">
                  <i className={`${service.icon} text-2xl text-brand-amber`} />
                </div>

                <h3 className="font-serif text-3xl lg:text-4xl font-bold text-brand-dark mb-4 leading-tight">
                  {service.title}
                </h3>
                <p className="text-brand-warm-gray text-base leading-relaxed mb-7 max-w-lg">
                  {service.shortDesc}
                </p>

                {/* Benefits grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-8">
                  {service.benefits.slice(0, 4).map((benefit) => (
                    <div key={benefit} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-double-line text-brand-amber text-sm" />
                      </div>
                      <span className="text-brand-light text-sm leading-snug">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* CTA row */}
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={`/services/${service.slug}`}
                    className="bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3 px-7 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    Learn More
                  </Link>
                  <Link
                    to="/contact"
                    className="flex items-center gap-2 text-brand-dark border-2 border-stone-200 hover:border-brand-amber hover:text-brand-amber font-bold py-3 px-6 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-check-line" />
                    </div>
                    Book This Service
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Post-services CTA */}
        <div className="mt-14 bg-brand-dark rounded-3xl p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-serif text-2xl lg:text-3xl font-bold text-white mb-2">
              Not sure which service you need?
            </h3>
            <p className="text-white/60 text-base">
              Our experts will assess your home and recommend the right solution — no pressure, no upsells.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link
              to="/contact"
              className="bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3.5 px-8 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap text-center"
            >
              Get a Free Quote
            </Link>
            <a
              href="tel:+19045550191"
              className="border-2 border-white/20 hover:border-white/50 text-white font-semibold py-3.5 px-7 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap text-center"
            >
              Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
